import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { AlertTriangle, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'

import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  type JobPosition,
  useCreateJobPosition,
  useDeleteJobPosition,
  useJobPositions,
  useUpdateJobPosition,
} from '@/features/admin/use-job-positions'

function pluralizePracownicy(n: number): string {
  if (n === 1) return '1 pracownikowi'
  return `${n} pracownikom`
}

const createSchema = z.object({
  name: z
    .string()
    .min(1, 'Wpisz nazwę stanowiska')
    .max(100, 'Maksymalnie 100 znaków'),
  sortOrder: z.coerce.number().int('Liczba całkowita'),
})

type CreateValues = z.infer<typeof createSchema>

export default function PositionsSettings() {
  const { data: positions, isLoading, error } = useJobPositions()
  const createMutation = useCreateJobPosition()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [toDelete, setToDelete] = useState<JobPosition | null>(null)

  const nextSortOrder = positions
    ? Math.max(0, ...positions.map((p) => p.sortOrder)) + 1
    : 1

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', sortOrder: nextSortOrder },
  })

  async function onCreate(values: CreateValues) {
    try {
      await createMutation.mutateAsync(values)
      toast.success('Stanowisko dodane')
      form.reset({ name: '', sortOrder: nextSortOrder + 1 })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Stanowiska</h2>
        <p className="mt-1 text-sm text-text-muted">
          Lista stanowisk pracowniczych w klinice. Po usunięciu stanowiska
          pracownicy zachowają puste pole — można je później ponownie przypisać.
        </p>
      </div>

      <section className="rounded-card border border-border-subtle bg-bg-elevated p-4">
        <form
          onSubmit={form.handleSubmit(onCreate)}
          noValidate
          className="flex flex-wrap items-end gap-3"
        >
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="new-position-name">Nazwa stanowiska</Label>
            <Input
              id="new-position-name"
              placeholder="Np. Asystentka stomatologiczna"
              invalid={!!form.formState.errors.name}
              {...form.register('name')}
            />
            {form.formState.errors.name && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>
          <div className="w-28">
            <Label htmlFor="new-position-order">Kolejność</Label>
            <Input
              id="new-position-order"
              type="number"
              invalid={!!form.formState.errors.sortOrder}
              {...form.register('sortOrder')}
            />
          </div>
          <Button type="submit" loading={createMutation.isPending}>
            <Plus className="h-4 w-4" aria-hidden />
            Dodaj
          </Button>
        </form>
      </section>

      {error && <Alert variant="error">{apiErrorMessage(error)}</Alert>}

      {isLoading && (
        <p className="text-sm text-text-muted inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Wczytuję stanowiska…
        </p>
      )}

      {!isLoading && positions && positions.length === 0 && (
        <div className="rounded-card border border-border-subtle bg-bg-elevated p-8 text-center text-text-muted">
          Brak stanowisk. Dodaj pierwsze powyżej.
        </div>
      )}

      {!isLoading && positions && positions.length > 0 && (
        <div className="rounded-card border border-border-subtle bg-bg-elevated overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-surface text-text-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Nazwa</th>
                  <th className="text-left px-4 py-3 font-medium w-24">
                    Kolejność
                  </th>
                  <th className="text-left px-4 py-3 font-medium w-32">
                    Pracownicy
                  </th>
                  <th className="text-right px-4 py-3 font-medium w-32">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) =>
                  editingId === p.id ? (
                    <PositionRowEditing
                      key={p.id}
                      position={p}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <PositionRowCompact
                      key={p.id}
                      position={p}
                      onEdit={() => setEditingId(p.id)}
                      onDelete={() => setToDelete(p)}
                    />
                  ),
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {toDelete && (
        <DeleteConfirmDialog
          position={toDelete}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  )
}

function PositionRowCompact({
  position,
  onEdit,
  onDelete,
}: {
  position: JobPosition
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <tr className="border-t border-border-subtle">
      <td className="px-4 py-3 font-medium text-text-primary">{position.name}</td>
      <td className="px-4 py-3 font-mono text-text-muted">{position.sortOrder}</td>
      <td className="px-4 py-3 text-text-muted">{position.userCount}</td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center h-8 w-8 rounded-action text-text-muted hover:text-text-primary hover:bg-bg-surface transition"
            aria-label={`Edytuj ${position.name}`}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center justify-center h-8 w-8 rounded-action text-text-muted hover:text-danger hover:bg-danger/5 transition"
            aria-label={`Usuń ${position.name}`}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </td>
    </tr>
  )
}

const editSchema = z.object({
  name: z.string().min(1, 'Wpisz nazwę').max(100, 'Maksymalnie 100 znaków'),
  sortOrder: z.coerce.number().int('Liczba całkowita'),
})
type EditValues = z.infer<typeof editSchema>

function PositionRowEditing({
  position,
  onCancel,
}: {
  position: JobPosition
  onCancel: () => void
}) {
  const update = useUpdateJobPosition()
  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: position.name, sortOrder: position.sortOrder },
  })

  async function onSubmit(values: EditValues) {
    const patch: { name?: string; sortOrder?: number } = {}
    if (values.name !== position.name) patch.name = values.name
    if (values.sortOrder !== position.sortOrder) patch.sortOrder = values.sortOrder
    if (Object.keys(patch).length === 0) {
      onCancel()
      return
    }
    try {
      await update.mutateAsync({ id: position.id, input: patch })
      toast.success('Stanowisko zaktualizowane')
      onCancel()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <tr className="border-t border-border-subtle bg-bg-surface/30">
      <td className="px-4 py-3" colSpan={4}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          noValidate
          className="flex flex-wrap items-end gap-3"
        >
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor={`edit-name-${position.id}`}>Nazwa</Label>
            <Input
              id={`edit-name-${position.id}`}
              invalid={!!form.formState.errors.name}
              {...form.register('name')}
            />
            {form.formState.errors.name && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>
          <div className="w-28">
            <Label htmlFor={`edit-order-${position.id}`}>Kolejność</Label>
            <Input
              id={`edit-order-${position.id}`}
              type="number"
              invalid={!!form.formState.errors.sortOrder}
              {...form.register('sortOrder')}
            />
          </div>
          <Button type="submit" loading={update.isPending}>
            Zapisz
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Anuluj
          </Button>
        </form>
      </td>
    </tr>
  )
}

function DeleteConfirmDialog({
  position,
  onClose,
}: {
  position: JobPosition
  onClose: () => void
}) {
  const remove = useDeleteJobPosition()

  async function onConfirm() {
    try {
      await remove.mutateAsync(position.id)
      toast.success('Stanowisko usunięte')
      onClose()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  const hasUsers = position.userCount > 0

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
    >
      <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle
            className={cn(
              'h-5 w-5 flex-shrink-0 mt-0.5',
              hasUsers ? 'text-warning' : 'text-text-muted',
            )}
            aria-hidden
          />
          <div>
            <h2 className="text-lg font-semibold mb-1">
              Usunąć stanowisko „{position.name}"?
            </h2>
            <p className="text-sm text-text-muted">
              {hasUsers
                ? `Stanowisko jest przypisane do ${pluralizePracownicy(position.userCount)}. Po usunięciu pole stanowiska zostanie wyczyszczone. Operacja jest nieodwracalna.`
                : 'Stanowisko nie jest przypisane do żadnego pracownika. Operacja jest nieodwracalna.'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            loading={remove.isPending}
            className="bg-danger text-bg-elevated hover:opacity-90"
          >
            Tak, usuń
          </Button>
        </div>
      </div>
    </div>
  )
}
